import React, { useEffect, useState } from 'react';
import {KatRow, KatColumn, KatDropdown, KatInput, KatDataTable, KatButton, KatPagination} from '@amzn/katal-react';
import './css/TellerTab.css';
import { DropDownHelper } from 'src/components/helper/DropDownHelper';
import {dollarglassLink, projectQuery, tellerPayableLink} from 'src/components/constants/constants';
import {
    serviceDropdownOptions,
    TESTS_EXECUTION_HEADER,
    TESTS_EXECUTION_HISTORY_HEADER,
    OFA_EXECUTION_HEADER
} from 'src/components/constants/dataTableHeaders';
import { TestCase } from 'src/components/types/TestCase';
import {
    fetchTestCases,
    fetchTestExecutions,
    initiatePayment,
    updateExecutionComment
} from 'src/components/helper/testCaseDataHelper';
import { TestExecution } from 'src/components/types/TestExecution';
import tellerIcon from 'src/images/tellerIcon.png';

const TestExecutionTab: React.FC = () => {
    const [service, setService] = useState<string>('Teller');
    const [project, setProject] = useState<string>('');
    const [testCases, setTestCases] = useState<TestCase[]>([]);
    const [filteredTestCases, setFilteredTestCases] = useState<TestCase[]>([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [testExecutions, setTestExecutions] = useState<TestExecution[]>([]);
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [selectedDescription, setSelectedDescription] = useState<string>('');
    const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(false);
    const [testcaseCurrentPage, setTestcaseCurrentPage] = useState(1);
    const [testcaseItemsPerPage, setTestcaseItemsPerPage] = useState(10);
    const [testExecutionsCurrentPage, setTestExecutionsCurrentPage] = useState(1);
    const [testExecutionsItemsPerPage, setTestExecutionsItemsPerPage] = useState(5);
    const [isSubmittingComment, setIsSubmittingComment] = useState<boolean>(false);

    const executeColumn = {
        label: 'Execute',
        formatter: (rowIndex: number, rowData: TestCase) => {
            const icon = document.createElement('kat-icon');
            icon.setAttribute('name', 'play');
            icon.setAttribute('size', 'small');
            icon.style.cursor = 'pointer';
            icon.style.color = 'blue';
            icon.onclick = (e) => {
                e.stopPropagation();
                const selectedTestCase = getTestIdFromEvent(e);
                if (rowData?.testId) {
                    const confirmed = window.confirm(`Submit payment for : ${rowData.description}?`);
                    if (!confirmed) return;
                    handleInitiatePayment(rowData.testId, rowData.project, rowData.description);
                }
            };
            return { element: icon };
        }
    };

    const handleInitiatePayment = async (testId: string, project: string, description: string) => {
        try {
            console.log('Initiating payment for testId:', testId);
            setIsSubmitting(true);

            const response = await initiatePayment(service, testId);
            console.log('Payment initiation response status:', response.status);

            const bodyText = await response.text();
            console.log('Payment response body:', bodyText);

            if (response.ok) {
                await loadTestExecutions(testId, project, description);
            } else {
                let parsed;
                try {
                    parsed = JSON.parse(bodyText);
                    console.log('Parsed error response:', parsed)
                } catch {
                    parsed = bodyText;
                    console.log('Parsed error response exception:', parsed)
                }

                const errorMessage = typeof parsed === 'string'
                    ? parsed
                    : parsed?.message || JSON.stringify(parsed);

                window.alert('Payment initiation failed: ' + errorMessage);
            }
        } catch (error) {
            console.error('Network error or exception during payment initiation:', error);
            window.alert('An unexpected error occurred during payment initiation.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const commentsColumn = {
        label: 'Comments',
        formatter: (rowIndex: number, rowData: TestExecution) => {
            const inputGroup = document.createElement('kat-input-group');
            inputGroup.style.display = 'flex';
            inputGroup.style.alignItems = 'center';
            inputGroup.style.gap = '0.5rem'; // optional spacing

            const commentInput = document.createElement('kat-input');
            commentInput.setAttribute('variant', 'text');
            commentInput.setAttribute('value', rowData.notes === null ? "" : rowData.notes);

            const icon = document.createElement('kat-icon');
            icon.setAttribute('name', 'save');
            icon.setAttribute('size', 'small')
            icon.style.cursor = 'pointer';
            icon.style.color = 'blue';
            icon.onclick = (e) => {
                e.stopPropagation();
                const selectedApServiceId = rowData.apServiceId;
                const confirmed = window.confirm(`Update comment for : ${rowData.apServiceId}?`);

                const selectedComment = commentInput.getAttribute('value') || ""; // fallback
                const finalComment = commentInput.shadowRoot?.querySelector('input')?.value || selectedComment;

                if (!confirmed) return;
                handleCommentUpdate(selectedApServiceId, finalComment);
            };

            const img = document.createElement("img");
            img.setAttribute("src", tellerIcon);
            img.setAttribute("alt", "Teller Preview");
            img.setAttribute("width", "18"); // Adjust width/height as needed
            img.setAttribute("height", "18");
            img.style.borderRadius = '4px';
            img.style.cursor = 'pointer';

            // Optionally, add click behavior to open in new tab
            img.addEventListener("click", () => {
                if (rowData.dollarGlassLink != null) {
                    window.open(tellerPayableLink + rowData.dollarGlassLink, "_blank");
                }
            });

            const searchIcon = document.createElement('kat-icon');
            searchIcon.setAttribute('name', 'search');
            searchIcon.setAttribute('size', 'small');
            searchIcon.style.cursor = 'pointer';
            searchIcon.style.color = 'blue';
            searchIcon.style.marginLeft = '0.25rem';

            searchIcon.addEventListener("click", () => {
                window.open(dollarglassLink + rowData.dollarGlassLink, "_blank");
            });

            inputGroup.appendChild(commentInput);
            inputGroup.appendChild(icon);
            if (rowData.dollarGlassLink != null) {
                if (service !== 'OFA') {
                    inputGroup.appendChild(img);
                }
                if (service !== 'Teller') {
                    inputGroup.appendChild(searchIcon);
                }
            }

            return { element: inputGroup };
        }
    };

    const baseExecutionColumns = service === 'OFA' ? OFA_EXECUTION_HEADER : TESTS_EXECUTION_HEADER;


    const executeTableColumns = [...baseExecutionColumns, executeColumn];
    const executionHistory = [...TESTS_EXECUTION_HISTORY_HEADER, commentsColumn];

    const handleCommentUpdate = async (apServiceId: string, comment: string) => {
        setIsSubmittingComment(true);
        try {
            const success = await updateExecutionComment(apServiceId, comment);
            if (success) {
                window.alert('Comment updated successfully.');

                // Refresh execution history
                const matchedExecution = testExecutions.find(exec => exec.apServiceId === apServiceId);
                if (matchedExecution) {
                    await loadTestExecutions(matchedExecution.testId, matchedExecution.project, selectedDescription);
                }
            } else {
                window.alert('Failed to update comment.');
            }
        } catch (error) {
            window.alert('An error occurred while updating the comment.');
        } finally {
            setIsSubmittingComment(false);
        }
    };

    const handleSearchChange = (term: string) => {
        setSearchTerm(term);
        applyFilters(testCases, project, term);
    };

    const applyFilters = (allCases: TestCase[], selectedProject: string, search: string) => {
        let result = [...allCases];
        if (selectedProject) {
            result = result.filter(tc => tc.project?.toLowerCase() === selectedProject.toLowerCase());
        }
        const lowerTerm = search.trim().toLowerCase();
        if (lowerTerm) {
            result = result.filter((test) =>
                Object.values(test).some((val) => {
                    if (typeof val !== 'string' && typeof val !== 'number') return false;
                    return String(val).toLowerCase().includes(lowerTerm);
                })
            );
        }
        setFilteredTestCases(result);
    };

    const loadTestCases = async (selectedService: string) => {
        const cases = await fetchTestCases(selectedService, true);
        setTestCases(cases);
        applyFilters(cases, '', searchTerm);
    };

    const loadTestExecutions = async (testId: string, project: string, description: string) => {
        setIsLoadingHistory(true);
        try {
            const executions = await fetchTestExecutions(testId, project);
            setTestExecutions(executions);
            setSelectedDescription(description);
        } catch (error) {
            window.alert('Error loading execution history');
        } finally {
            setIsLoadingHistory(false); // hide overlay
        }
    };

    const handleRowClick = (e: any) => {
        const selectedTestCase = getTestIdFromEvent(e);
        if (selectedTestCase?.testId) {
            loadTestExecutions(selectedTestCase.testId, selectedTestCase.project, selectedTestCase.description);
        }
    };

    const getTestIdFromEvent = (e: any) => {
        const row = e.target.closest('tr');
        const tdElements = row?.getElementsByTagName('td');
        if (tdElements && tdElements[0]) {
            const value = tdElements[0].textContent || tdElements[0].innerText;
            return testCases.find((tc) => tc.description === value);
        }
        return null;
    };

    useEffect(() => {
        if (!service) return;
        setProject('');
        loadTestCases(service);
    }, [service]);

    useEffect(() => {
        applyFilters(testCases, project, searchTerm);
    }, [project]);

    const updatedProjectQuery = `${projectQuery}?service=${encodeURIComponent(service)}`;

    const paginatedTestCases = filteredTestCases.slice(
        (testcaseCurrentPage - 1) * testcaseItemsPerPage,
        testcaseCurrentPage * testcaseItemsPerPage
    );

    const paginatedTestExecutions = testExecutions.slice(
        (testExecutionsCurrentPage - 1) * testExecutionsItemsPerPage,
        testExecutionsCurrentPage * testExecutionsItemsPerPage
    );

    return (
        <div className="test-case-section overlay-wrapper">
            <KatRow className="form-row">
                <KatColumn xs={2}>
                    <KatDropdown
                        options={serviceDropdownOptions}
                        label="Service"
                        value={service}
                        onChange={(e: CustomEvent) => setService(e.detail.value)}
                    />
                </KatColumn>
                <KatColumn xs={4}>
                    <DropDownHelper
                        query={updatedProjectQuery}
                        label="Project"
                        value={project}
                        onChange={setProject}
                    />
                </KatColumn>
                <KatColumn xs={4} />
                <KatColumn xs={2}>
                    <KatButton type="submit" variant="primary" className="button" label="Execute All" />
                </KatColumn>
            </KatRow>

            <KatRow className="form-row">
                <KatColumn xs={12}>
                    <KatInput
                        type="search"
                        placeholder="Search..."
                        value={searchTerm}
                        onInput={(e: KatInput.ChangeEvent) => handleSearchChange(e.currentTarget.value)}
                        size="small"
                    />
                </KatColumn>
            </KatRow>

            <KatRow>
                <KatColumn xs={12}>
                    <KatDataTable
                        titleLabel="Available Tests"
                        columns={executeTableColumns}
                        rowData={paginatedTestCases}
                        onClick={handleRowClick}
                        config="enableSelectionColumn: true"
                    />
                    <KatPagination
                        page={testcaseCurrentPage}
                        totalItems={filteredTestCases.length}
                        itemsPerPage={testcaseItemsPerPage}
                        variant="footer"
                        onChange={(e) => setTestcaseCurrentPage(e.detail.page)}
                        onItemsPerPageChange={(e) => {
                            setTestcaseItemsPerPage(e.detail.value);
                            setTestcaseCurrentPage(1); // Reset to first page when changing page size
                        }}
                    />
                </KatColumn>
            </KatRow>

            {testExecutions.length > 0 && (
                <KatRow>
                    <KatColumn xs={12}>
                        <KatDataTable
                            titleLabel={`Execution History${selectedDescription ? `: ${selectedDescription}` : ''}`}
                            columns={executionHistory}
                            rowData={paginatedTestExecutions}
                        />
                        <KatPagination
                            page={testExecutionsCurrentPage}
                            totalItems={testExecutions.length}
                            itemsPerPage={testExecutionsItemsPerPage}
                            variant="footer"
                            onChange={(e) => setTestExecutionsCurrentPage(e.detail.page)}
                            onItemsPerPageChange={(e) => {
                                setTestExecutionsItemsPerPage(e.detail.value);
                                setTestExecutionsCurrentPage(1); // Reset to first page when changing page size
                            }}
                        />
                    </KatColumn>
                </KatRow>
            )}

            {(isSubmitting || isLoadingHistory || isSubmittingComment) && (
                <div className="overlay">
                    <div className="overlay-message">
                        {isSubmitting
                            ? 'Initiating payment…'
                            : isSubmittingComment
                                ? 'Updating comment…'
                                : 'Loading execution history…'}
                    </div>
                </div>
            )}
        </div>
    );
};

export default TestExecutionTab;